import { request } from "@/common";
import { makeAutoObservable, reaction } from "mobx";
import { GenerateVideoStore } from '@/pages/generate-video/store';
import MarketingStore from '@/store/marketing';
import { deepCopy, type } from "windicss/utils";
import { HelperCore } from "@/common/dependencies";
import { HelpOther } from "@/common/helper";
import appLogger from "@public/log/monitor";
import client from "@/store/client";
import client360Store from "@public/client/client360";
import stateStore from "@/pages/square-video/state-store";

type BgmListResponse<M = { key: 'favorite', timestamp: string }> = {
  bgms: { cover: string, id: string, marks: M[], tags: string[], title: string, voice_url: string }[],
  marks_and_tags: { key: string, name: string }[],
  tags: { key: string, name: string }[],
}

export const aigcRatio = [{
  icon: '',
  val: '',
  label: '原比例',
  valUnderline: 'original'
}, {
  icon: 'ratio_9_16',
  val: '9:16',
  label: '9 : 16',
  valUnderline: '9_16'
}, {
  icon: 'ratio_3_4',
  val: '3:4',
  label: '3 : 4',
  valUnderline: '3_4'

}, {
  icon: 'ratio_1_1',
  val: '1:1',
  label: '1 : 1',
  valUnderline: '1_1'

}, {
  icon: 'ratio_4_3',
  val: '4:3',
  label: '4 : 3',
  valUnderline: '4_3'

}, {
  icon: 'ratio_16_9',
  val: '16:9',
  label: '16 : 9',
  valUnderline: '16_9'
}]


const PUBLIC_PARAMS = {
  deepseek_optimize: 0,
  prompt: '',
  image: '',
}

const GEN_IMG = {
  ...PUBLIC_PARAMS,
  image_ratio: '',
  generated_num: 4,
  model_key: '',
  // 风格id
  image_style: '',
  width: 0,
  height: 0,
  biz_id: '',
};

const GEN_VIDEO_PUBLIC_PARAMS = {
  prompt: '',
  img1: '',
  img2: '',
  img_n: [] as string[],
  bgm_id: '',
  ratio: '',
  duration: 4,
}

const GEN_EFFECT_VIDEO = {
  prompt: '',
  bgm_id: '',
  apply_model: '',
  ratio: '',
  duration: 4,
  video_id: '',
  method: '',
  mode: '',
  template_id: '',
}

const GEN_VIDEO = {
  apply_model: '',
  deepseek_optimize: 0,
  video_id: '',
  method: '',
  mode: '',
  template_id: '',
  gen_num: 1,
  resolution: ''
};


export type FormType = 'text_video' | 'image_video' | 'text_image' | 'image_image' | 'effect_video' | 'image_video_part' | 'image_video_reference' | 'image_video_frame' | 'video_public' | 'image_public'
type ListType = 'bgm' | 'style' | 'video-model' | 'img-model' | 'effect'

class AigcFormStore {
  imgImgForm: typeof GEN_IMG = deepCopy({ ...GEN_IMG });
  textImgForm: typeof GEN_IMG = deepCopy({ ...GEN_IMG });
  imgPublicForm: typeof GEN_IMG = deepCopy({ ...GEN_IMG });

  textVideoForm: typeof GEN_VIDEO = deepCopy({ ...GEN_VIDEO });

  videoPublicForm: typeof GEN_VIDEO_PUBLIC_PARAMS = deepCopy(GEN_VIDEO_PUBLIC_PARAMS);
  // 单图
  imgVideoForm: typeof GEN_VIDEO = deepCopy(GEN_VIDEO);
  // 多图
  imgVideoReferenceForm: typeof GEN_VIDEO = deepCopy({ ...GEN_VIDEO, mode: 'reference',  });
  // 视频帧
  imgVideoFrameForm = deepCopy({ ...GEN_VIDEO, frame_img: '', frame_prompt: '', mode: 'frame' });
  // 首尾帧
  imgVideoPartForm: typeof GEN_VIDEO = deepCopy({ ...GEN_VIDEO, mode: 'part' });

  effectForm: typeof GEN_EFFECT_VIDEO = deepCopy(GEN_EFFECT_VIDEO);

  isVideoListLoaded = false;
  isImgListLoaded = false

  cachedRecommendedPromptMap = {}

  lastRecommendedPromptRequest;

  listData = {
    vdDurationList: [],
    vdRatioList: aigcRatio.filter(item => item.val === '16:9' || item.val === '1:1' || item.val === '9:16'),
    musicMap: { bgms: [], marks_and_tags: [], } as BgmListResponse,
    styleList: [],
    videoModelList: [],
    imgModelList: [],

    imgModelShownList: [],

    imgRatioList: {
      text_image: [],
      image_image: []
    },

    imgNumList: {
      text_image: [],
      image_image: []
    },

    videoRatioList: {
      image_video: [],
      text_video: []
    },
    videoDurationList: {
      image_video: [],
      text_video: []
    },
    templateList: [],
    templateCoincfg: {
      cost: 0,
      member_cost: 0,
    },
    memberVideoModelMap: {
      list: [],
      map: {
        expressway_usable: '',
        no_watermark_usable: '',
        nano_consume_baseline: {
          member_cost: 0,
          cost: 0,
        }
      },
    }
  }


  shownDropdown: 'bgm' | 'style' | 'video-model' | 'ds' | '' = '';

  get musicMap() {
    const { musicMap } = this.listData;
    const ret = {
      all: musicMap.bgms,
      favorite: [],
      used: [],
      ...musicMap.bgms.reduce((prev, cur) => {
        cur.tags.forEach(tag => {
          if (prev[tag]) {
            prev[tag].push(cur)
          } else {
            prev[tag] = [cur]
          }
        })
        cur.marks.forEach(mark => {
          if (prev[mark.key]) {
            prev[mark.key].push(cur)
          } else {
            prev[mark.key] = [cur]
          }
        })
        return prev
      }, {})
    } as { [key: string]: BgmListResponse['bgms'] }

    return ret
  }

  constructor() {
    makeAutoObservable(this);
    if (import.meta.env.MODE === 'dev') {
      window.aigcFormStore = this
    }
  }

  setCachedRecommendedPromptMap(item, url) {
    this.cachedRecommendedPromptMap[url] = item
  }
  setShownDropdown(type: typeof this['shownDropdown']) {
    this.shownDropdown = type;
  }
  formTypeToApplyModel(type: 'image_image' | 'text_image') {
    switch (type) {
      case 'image_image': {
        return 'image_to_image'
      }
      case 'text_image': {
        return 'text_to_image'
      }
    }
  }

  isSameType(type: 'image_image' | 'text_image', applyModel: 'all' | 'image_to_image' | 'text_to_image') {
    return this.formTypeToApplyModel(type) === applyModel || applyModel === 'all'
  }

  toggleBGMMarks(item) {
    if (item.marks.some(mark => mark.key === 'favorite')) {
      item.marks = item.marks.filter(m => m.key !== 'favorite')
    } else {
      item.marks = [...item.marks, { key: 'favorite', timestamp: Date.now() }]
    }
  }

  #modelFallback(imageModelList, formType: FormType) {
    if (!imageModelList?.length) {
      return
    }

    // 需求要求不同mid尾号对应不同的模型选中态，所以这里加入如下处理：
    const mid = client.isLLQ ? client360Store.getMid() : HelperCore.getMid()
    const lastCharOfMid = mid[mid.length - 1]
    // 如果mid尾号在其中一个模型里
    if (MarketingStore.isMarketingCloud) {
      const item = imageModelList.find(model => model.pc_default_strategy_mid.includes(lastCharOfMid))
      if (item) {
        this.#setForm(formType, { model_key: item.model_id })
        this.formListLink(formType, 'img-model')
        return;
      }
    }

    // 如果mid尾号不在其中一个模型里, 则兜底
    const activeModelKey = this.getFormValue(formType, 'model_key')
    if (!activeModelKey && formType || imageModelList.some(m => m.model_id !== activeModelKey)) {
      this.#setForm(formType, { model_key: imageModelList?.[0]?.model_id })
      this.formListLink(formType, 'img-model')
    }
  }

  async fetchImgListData() {

    const imageModelList = await request.get('/img/model/list', {
      params: {
        appsource: 'so',
        device: 1
      }
    })

    this.listData.imgModelList = imageModelList.data

    const styleList = await request.get('/img/style/list', {
      params: {
        appsource: 'so',
        device: 1
      }
    })

    this.listData.styleList = styleList.data

    this.fillImgFormData()
  }

  fetchTemplateList = async () => {
    if (this.listData.templateList?.length) {
      return this.listData.templateList;
    }
    const templateList = await request.get('/video/template/list/v2', {
      params: {
        appsource: 'so',
        device: 1
      }
    })
  
    // const templateList2 = await request.get('/video/template/list', {
    //   params: {
    //     appsource: 'so',
    //     device: 1
    //   }
    // })
    this.listData.templateList = templateList.data.list;
    this.listData.templateCoincfg = templateList.data.map.coin_cfg
    return templateList.data.list;
    // this.fillEffectFormData()
  }
  async fetchBgmList() {
    if (!this.listData.musicMap?.bgms.length) {
      const musicMap = await request.get<BgmListResponse>('/video/bgm/list/v2', {
        params: {
          appsource: 'so',
          with_mark: true
        }
      })
      musicMap.data.marks_and_tags.unshift({ key: 'all', name: '全部' })

      this.listData.musicMap = {
        ...musicMap.data, bgms: musicMap.data.bgms
        // .map(i => {
        //   i.marks.forEach(mark => {
        //     if (mark.key === 'favorite') {
        //       i.favorite
        //     }
        //   })
        //   return { ...i, marks: i.marks.map(m => m.key) }
        // })
      }

    }
  }
  async fetchVideoListData() {

    await this.fetchBgmList();
    // 营销云
    const videoModelList = await request.get('/video/model/list', {
      params: {
        appsource: 'so',
      }
    })
    this.listData.videoModelList = videoModelList.data
    const videoModelMap = await request.get('/video/model/cfg', {
      params: {
        appsource: 'so',
        device_platform: 'Web'
      }
    });
    this.listData.memberVideoModelMap = videoModelMap.data;
    this.fillVideoFormData();
  }

  fillImgFormData() {
    // if (!this.imgImgForm.image_style) {
    //   this.imgImgForm.image_style = this.listData.styleList[0]?.style_id
    //   this.formListLink('image_image', 'style')
    // }
    const formType = location.href.includes('text_image') ? 'text_image' : 'image_image'

    if (!this.imgPublicForm.image_style) {
      this.imgPublicForm.image_style = this.listData.styleList.find(item => this.isSameType(formType, item.apply_createimage))?.style_id
      this.formListLink(formType, 'style')
    }
  }

  fillEffectFormData() {
    if (this.listData.templateList?.length) {
      GenerateVideoStore.setCoverInfo({
        title: this.listData.templateList[0]?.title,
        icon: 'https://p5.ssl.qhimg.com/t110b9a9301231ae0c611484445.png',
        url: this.listData.templateList[0]?.video,
        describe: this.listData.templateList[0]?.desc,
        cover: this.listData.templateList[0]?.banner,
      })
      if (!this.effectForm.template_id && this.listData.templateList[0]?.id) {
        if (!this.videoPublicForm.img1) {
          this.videoPublicForm.img1 = this.listData.templateList[0]?.img_1_able ? '' : this.listData.templateList[0]?.img_1;
        }
        if (!this.videoPublicForm.img2) {
          this.videoPublicForm.img2 = this.listData.templateList[0]?.img_2_able ? '' : this.listData.templateList[0]?.img_2;
        }
        this.effectForm.template_id = this.listData.templateList[0]?.id
        this.effectForm.prompt = this.listData.templateList[0]?.prompt
        this.effectForm.apply_model = this.listData.templateList[0]?.model_name
        this.effectForm.bgm_id = this.listData.templateList[0]?.voice_id
        this.effectForm.duration = +this.listData.templateList[0]?.duration.split(',')[0]
      }
    }
  }
  fillVideoFormData() {
    // 设备mid尾号进行匹配，若匹配中，则默认选中该模型
    const mid = client.isLLQ ? client360Store.getMid() : HelperCore.getMid()
    const lastCharOfMid = mid[mid.length - 1]
    const type = location.href.includes('image_video') ? 'iv2' : 't2v';
    const activeModel =  this.listData.memberVideoModelMap.list.find(item => {
      const deviceIdentifierConfig = JSON.parse(item.device_identifier_config || '{}')
      return deviceIdentifierConfig[type]?.pc?.includes(lastCharOfMid);
    })

    if (!this.videoPublicForm.bgm_id) {
      this.videoPublicForm.bgm_id = this.listData.musicMap.bgms[0]?.id
    }

    if (!this.imgVideoPartForm.apply_model) {
       if (MarketingStore.isMarketingCloud) {
          this.imgVideoPartForm.apply_model = 'vidu'
       } else {
        this.imgVideoPartForm.apply_model = activeModel?.model_key || this.listData.memberVideoModelMap.list[0]?.model_key
       }
      this.formListLink('image_video_part', 'video-model')
    }
    if (!this.imgVideoReferenceForm.apply_model) {
       if (MarketingStore.isMarketingCloud) {
          this.imgVideoReferenceForm.apply_model = 'vidu'
       } else {
          this.imgVideoReferenceForm.apply_model = activeModel?.model_key || this.listData.memberVideoModelMap.list[0]?.model_key
       }
      this.formListLink('image_video_reference', 'video-model')
    }
    if (!this.imgVideoFrameForm.apply_model) {
       if (MarketingStore.isMarketingCloud) {
        this.imgVideoFrameForm.apply_model = this.listData.videoModelList[0]?.model_name
       } else {
        this.imgVideoFrameForm.apply_model = activeModel?.model_key || this.listData.memberVideoModelMap.list[0]?.model_key
       }
      this.formListLink('image_video_frame', 'video-model')
    }
    if (!this.imgVideoForm.apply_model) {
      if (MarketingStore.isMarketingCloud){
        this.imgVideoForm.apply_model = this.listData.videoModelList[0]?.model_name
      } else {
        this.imgVideoForm.apply_model = activeModel?.model_key || this.listData.memberVideoModelMap.list[0]?.model_key
      }
      this.formListLink('image_video', 'video-model')
    }
    if (!this.textVideoForm.apply_model) {
       if (MarketingStore.isMarketingCloud) {
          this.textVideoForm.apply_model = this.listData.videoModelList[0]?.model_name
       } else {
        this.textVideoForm.apply_model = activeModel?.model_key || this.listData.memberVideoModelMap.list[0]?.model_key
       }
      this.formListLink('text_video', 'video-model')
    }
  }

  async fetchListData(type: 'img' | 'video', formType) {
    if (!formType) {
      return;
    }
    if (type === 'img') {
      await this.fetchImgListData()
    } else if (formType.includes('effect')) {
      await this.fetchTemplateList()
      await this.fetchVideoListData();
    } else {
      await this.fetchVideoListData();
    }
  }

  getActiveItem(formType: FormType, type: ListType) {
    const form = this.getFormByType(formType)
    if (type === 'bgm' && 'bgm_id' in form) {
      return this.listData.musicMap?.bgms.find(item => item.id === form.bgm_id)
    }
    if (type === 'video-model' && 'apply_model' in form) {
      return this.listData.videoModelList.find(item => item.model_name === form.apply_model)
    }
    if (type === 'img-model' && 'model_key' in form) {
      return this.listData.imgModelShownList.find(item => item.model_id === form.model_key)
    }
    if (type === 'style' && 'image_style' in form) {
      return this.listData.styleList.find(item => item.style_id === form.image_style)
    }
    if (type === 'effect' && 'template_id' in form) {
      return this.listData.templateList.find(item => item.id === form.template_id)
    }
  }

  // 表单联动
  formListLink(formType: FormType, listType: ListType) {
    switch (listType) {
      case 'style': {
        console.log('风格变化了嗷，触发模型列表和选中态变化')
        // 风格变化触发模型列表变化
        const type = formType as 'image_image' | 'text_image'

        const styleItem = this.getActiveItem(type, 'style')
        // 先更新模型列表选项
        this.listData.imgModelShownList = this.listData.imgModelList.filter(item => {
          // console.error(type, item.apply_model, this.isSameType(type, item.apply_model),styleItem.apply_model, styleItem.apply_model.includes(item.model_id), item.model_id)
          return this.isSameType(type, item.apply_model) && styleItem.apply_model.includes(item.model_id)
        }
        )
        // 万一配的自相矛盾了，用豆包兜底，如：风格是图生图风格 风格只支持混元 混元是文生图模型 
        if (!this.listData.imgModelShownList.length) {
          this.listData.imgModelShownList = this.listData.imgModelList.filter(item => item.model_id === 'doubao')
        }
        // 如果当前选项值匹配不上新列表, 才重新设置
        if (
          !this.listData.imgModelShownList
            .find(item =>
              item.model_id === this.imgPublicForm.model_key
              && styleItem.apply_model.includes(item.model_id)
            )
        ) {
          this.#modelFallback(this.listData.imgModelShownList, formType)
        }

        // 模型列表变化后的联动
        this.formListLink(formType, 'img-model')
        break;
      }
      case 'img-model': {
        console.log('模型列表变化了嗷，触发比例和数量列表和选中态变化')
        // 图片模型变化触发图片比例、张数变化
        const type = formType as 'image_image' | 'text_image'

        const modelItem = this.getActiveItem(type, 'img-model')

        // 先更新张数、比例列表选项
        this.listData.imgRatioList.image_image = aigcRatio.filter(item => modelItem?.image_scale.find(ratio => ratio === item.valUnderline))
        this.listData.imgRatioList.text_image = aigcRatio.filter(item => modelItem?.text_scale.find(ratio => ratio === item.valUnderline))
        this.listData.imgNumList.image_image = modelItem?.image_counts.map(Number) || []
        this.listData.imgNumList.text_image = modelItem?.text_counts.map(Number) || []

        const ratioVal = this.getFormValue(type, 'image_ratio');

        if (type === 'image_image') {
          const activeItem = this.listData.imgRatioList.image_image.find(item => item.valUnderline === modelItem.image_scale_default)
          if (!ratioVal || !this.listData.imgRatioList.image_image.find(item => item.val === ratioVal)) {

            // 二重兜底 如果cms配错了走这里
            if (!activeItem) {
              this.#setForm(formType, { image_ratio: this.listData.imgRatioList.image_image[0]?.val })
            } else {
              this.#setForm(formType, { image_ratio: activeItem.val })
            }
          }
        }
        if (type === 'text_image') {
          const activeItem = this.listData.imgRatioList.text_image.find(item => item.valUnderline === modelItem.text_scale_default)
          if (!ratioVal || !this.listData.imgRatioList.text_image.find(item => item.val === ratioVal)) {

            // 二重兜底 如果cms配错了走这里
            if (!activeItem) {
              this.#setForm(formType, { image_ratio: this.listData.imgRatioList.text_image[0]?.val })
            } else {
              this.#setForm(formType, { image_ratio: activeItem.val })
            }
          }
        }

        const numVal = this.getFormValue(type, 'generated_num');

        if (type === 'image_image') {
          if (!numVal || !this.listData.imgNumList.image_image?.find(item => numVal === item)) {
            this.#setForm(formType, { generated_num: +modelItem?.image_counts_default })
          }
        }
        if (type === 'text_image') {
          if (!numVal || !this.listData.imgNumList.text_image?.find(item => numVal === item)) {
            this.#setForm(formType, { generated_num: +modelItem?.text_counts_default })
          }
        }

        // 如果当前选项值匹配不上新列表, 重置表单值
        if (!this.listData.imgRatioList[type]?.find(item => item.val === this.imgPublicForm.image_ratio)) {
          this.#setForm(formType, { image_ratio: this.listData.imgRatioList[type]?.[0]?.val })
        }
        if (!this.listData.imgNumList[type]?.find(item => item === this.imgPublicForm.generated_num)) {
          this.#setForm(formType, { generated_num: this.listData.imgNumList[type]?.[0] })
        }
        break;
      }

      case 'video-model': {
        const type = formType as 'image_video' | 'text_video' | 'image_video_part' | 'image_video_reference' | 'image_video_frame' | 'video_public'
        const modelItem = this.getActiveItem(type, 'video-model')
        const currentForm = this.getFormByType<typeof type>(type)
        const publicForm = this.getFormByType('video_public')
        // 先更新张数、比例列表选项
        this.listData.videoRatioList.text_video = aigcRatio.filter(item => modelItem?.text_ratio.find(ratio => ratio === item.val))
        this.listData.videoRatioList.image_video = aigcRatio.filter(item => modelItem?.ratio.find(ratio => ratio === item.val))
        this.listData.videoDurationList.text_video = modelItem?.text_duration.map(Number)
        this.listData.videoDurationList.image_video = modelItem?.duration.map(Number)
        const vd = this.listData.videoModelList.find(item => item.model_name === 'vidu')
        // 防止订阅模式表单数据被覆盖
        if (!MarketingStore.isMarketingCloud) {
          return;
        }
        if (type === 'image_video_reference' || type === 'image_video_part') {
          this.listData.vdDurationList = vd?.duration.map(Number) || []
          if (!this.listData.vdDurationList
            ?.find(item => 'duration' in publicForm && item === publicForm.duration)) {
            this.#setForm('video_public', { duration: this.listData.vdDurationList?.[0] })
          }
          if (!this.listData.vdRatioList
            ?.find(item => 'ratio' in publicForm && item.val === publicForm.ratio)) {
            this.#setForm('video_public', { ratio: this.listData.vdRatioList?.[0]?.val })
          }
          return
        }
        // 如果当前选项值匹配不上新列表, 重置表单值
        if ((type === 'text_video' && this.listData.videoRatioList.text_video?.length && !this.listData.videoRatioList.text_video
          ?.find(item => 'ratio' in publicForm && item.val === publicForm.ratio))
        ) {
          this.#setForm('video_public', { ratio: this.listData.videoRatioList.text_video?.[0]?.val })
        }

        if ((type === 'image_video' || type === 'image_video_frame') && this.listData.videoRatioList.image_video?.length && !this.listData.videoRatioList.image_video
          ?.find(item => 'ratio' in publicForm && item.val === publicForm.ratio)
        ) {
          this.#setForm('video_public', { ratio: this.listData.videoRatioList.image_video?.[0]?.val })
        }

        if (type === 'text_video' && this.listData.videoDurationList.text_video?.length &&
          !this.listData.videoDurationList.text_video?.find(item => 'duration' in publicForm && item === publicForm.duration)) {
          this.#setForm('video_public', { duration: this.listData.videoDurationList.text_video?.[0] })
        }
        if ((type === 'image_video' || type === 'image_video_frame') && this.listData.videoDurationList.image_video?.length &&
          !this.listData.videoDurationList.image_video?.find(item => 'duration' in publicForm && item === publicForm.duration)) {
          this.#setForm('video_public', { duration: this.listData.videoDurationList.image_video?.[0] })
        }
        break;
      }

      case 'effect': {
        const type = formType as 'effect_video'
        const templateItem = this.getActiveItem(type, 'effect')
      }
    }
  }

  async fetchRecommendedPrompt(imgURL: string) {
    if (this.lastRecommendedPromptRequest) {
      return this.lastRecommendedPromptRequest
    }
    this.lastRecommendedPromptRequest = request.post('/video/batch_suggest_prompt?appsource=so', {
      image_url: imgURL,
      device: 1
    }, { dataIsJson: true })
      .then(res => res.data).finally(() => {
        this.lastRecommendedPromptRequest = void 0;
      })
    return this.lastRecommendedPromptRequest;
  }

  async fetchRemainingCounter(type: 'image' | 'video') {
    const res = await request.get('/img/remaining/counter?appsource=so', {
      params: {
        func_type: type === 'image' ? 0 : 1
      }
    })
    return res.data.remaining_counter
  }

  get formMap() {
    return {
      image_image: this.imgPublicForm,
      text_image: this.imgPublicForm,
      text_video: this.textVideoForm,
      image_video: this.imgVideoForm,
      effect_video: this.effectForm,
      image_video_part: this.imgVideoPartForm,
      image_video_reference: this.imgVideoReferenceForm,
      image_video_frame: this.imgVideoFrameForm,
      video_public: this.videoPublicForm,
      image_public: this.imgPublicForm,
    } as const;
  }

  getFormByType<T extends FormType>(formType: T): (typeof this.formMap)[T] {

    return this.formMap[formType];
  }

  #setForm(
    formType: FormType,
    formData?: Partial<typeof GEN_VIDEO | typeof GEN_IMG | typeof GEN_VIDEO | typeof PUBLIC_PARAMS | typeof GEN_VIDEO_PUBLIC_PARAMS | { frame_img: string, frame_prompt: string }>
  ) {
    switch (formType) {
      case 'image_image': {
        Object.keys(this.imgPublicForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgPublicForm[key] = formData[key]
        })
        break;
      }
      case 'text_image': {
        Object.keys(this.imgPublicForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgPublicForm[key] = formData[key]
        })
        break;
      }
      case 'text_video': {
        Object.keys(this.textVideoForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.textVideoForm[key] = formData[key]
        })
        break;
      }
      case 'image_video': {
        Object.keys(this.imgVideoForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgVideoForm[key] = formData[key]
        })
        break;
      }
      case 'effect_video': {
        Object.keys(this.effectForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.effectForm[key] = formData[key]
        })
        break;
      }
      case 'image_video_part': {
        Object.keys(this.imgVideoPartForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgVideoPartForm[key] = formData[key]
        })
        break;
      }
      case 'image_video_reference': {
        Object.keys(this.imgVideoReferenceForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgVideoReferenceForm[key] = formData[key]
        })
        break;
      }
      case 'image_video_frame': {
        Object.keys(this.imgVideoFrameForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgVideoFrameForm[key] = formData[key]
        })
        break;
      }
      case 'image_public': {
        Object.keys(this.imgPublicForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.imgPublicForm[key] = formData[key]
        })
        break;
      }
      case 'video_public': {
        Object.keys(this.videoPublicForm).forEach(key => {
          if (!(key in formData)) {
            return
          }
          this.videoPublicForm[key] = formData[key]
        })
        break;
      }
    }
  }

  resetForm(formType) {
    console.log('重置了表单')
    switch (formType) {
      case 'image_image': {
        this.imgPublicForm = deepCopy(GEN_IMG);
        break;
      }
      case 'text_image': {
        this.imgPublicForm = deepCopy(GEN_IMG);
        break;
      }
      case 'text_video': {
        this.textVideoForm = deepCopy({ ...PUBLIC_PARAMS, ...GEN_VIDEO });
        break;
      }
      case 'image_video': {
        this.imgVideoForm = deepCopy({ ...PUBLIC_PARAMS, ...GEN_VIDEO });
        break;
      }
      case 'effect_video': {
        this.effectForm = deepCopy(GEN_EFFECT_VIDEO);
        break;
      }
      case 'video_public': {
        this.videoPublicForm = deepCopy(GEN_VIDEO_PUBLIC_PARAMS);
        break;
      }
      case 'image_video_reference': {
        this.imgVideoReferenceForm = deepCopy(GEN_VIDEO);
        break;
      }
      case 'image_video_frame': {
        this.imgVideoFrameForm = deepCopy({ ...GEN_VIDEO, frame_img: '', frame_prompt: '' });
        break;
      }
      case 'image_video_part': {
        this.imgVideoPartForm = deepCopy(GEN_VIDEO);
        break;
      }
      case 'image_public': {
        this.imgPublicForm = deepCopy(GEN_IMG);
        break;
      }
    }

    if (formType.includes('video')) {
      this.fillVideoFormData()
    } else {
      this.fillImgFormData()
    }
  }

  getFormValue(
    formType: FormType,
    key: keyof (typeof GEN_VIDEO & typeof GEN_IMG & typeof PUBLIC_PARAMS & typeof GEN_VIDEO & typeof GEN_VIDEO_PUBLIC_PARAMS & { frame_img: string, frame_prompt: string })
  ) {
    const form = this.getFormByType(formType)
    if (!form) {
      return void 0
    }
    if (key in form) {
      return form[key]
    }
  }

  // 处理表单联动
  handleListLink(
    formType: FormType,
    formData?: Partial<typeof GEN_VIDEO | typeof GEN_IMG | typeof PUBLIC_PARAMS | { frame_img: string, frame_prompt: string }>
  ) {
    if (!formType) {
      return
    }
    if ('image_style' in formData) {
      if (!this.listData.styleList.find(item => item.style_id === formData.image_style && this.isSameType(formType as 'image_image' | 'text_image', item.apply_createimage))) {
        this.#setForm(formType, { image_style: this.listData.styleList.find(item => this.isSameType(formType as 'image_image' | 'text_image', item.apply_createimage))?.style_id })
      }
      this.formListLink(formType, 'style')
    }
    if ('model_key' in formData) {
      if (!this.listData.imgModelShownList.find(item => item.model_id === formData.model_key)) {
        this.#setForm(formType, { model_key: this.listData.imgModelShownList?.[0]?.model_id })
      }
      this.formListLink(formType, 'img-model')
    }
    if (formType.includes('video') && 'apply_model' in formData) {
      if (!this.listData.videoModelList.find(item => item.model_name === formData.apply_model)) {
        if (MarketingStore.isMarketingCloud) {
          this.#setForm(formType, { apply_model: this.listData.videoModelList?.[0]?.model_name })
        }
      }
      this.formListLink(formType, 'video-model')
    }
    if ('template_id' in formData) {

      this.formListLink(formType, 'effect')
    }
  }

  setForm(
    formType: FormType,
    formData?: Partial<typeof GEN_VIDEO | typeof GEN_IMG | typeof GEN_VIDEO | typeof PUBLIC_PARAMS | typeof GEN_VIDEO_PUBLIC_PARAMS | { frame_img: string, frame_prompt: string }>
  ) {
    if (!formType) {
      return
    }
    this.#setForm(formType, formData)

    if (formType.includes('effect')) {
      this.fillEffectFormData()
      this.fillVideoFormData()
    } else if (formType.includes('video')) {
      this.fillVideoFormData()
    } else {
      this.fillImgFormData()
    }
    this.handleListLink(formType, formData)
    console.log('form', this.getFormByType(formType))
  }

  validate(formType: FormType) {
    const form = this.getFormByType(formType)
    const videoPublicForm = this.getFormByType('video_public')
    const imgPublicForm = this.getFormByType('image_public')
    let message = ''
    switch (formType) {
      case 'image_image': {
        if ('prompt' in imgPublicForm && !imgPublicForm.prompt.trim()) {
          message = '请输入提示词'
        }
        if ('image' in imgPublicForm && !imgPublicForm.image) {
          message = '请上传图片'
        }
        if ('image' in imgPublicForm && imgPublicForm.image.includes('violation')) {
          message = '图片内容违规，请更换图片'
        }
        break;
      }
      case 'text_image': {
        if ('prompt' in imgPublicForm && !imgPublicForm.prompt.trim()) {
          message = '请输入提示词'
        }
        break;
      }
      case 'text_video': {
        if ('prompt' in videoPublicForm && !videoPublicForm.prompt.trim()) {
          message = '请输入提示词'
        }
        break;
      }
      case 'image_video': {
        if (!videoPublicForm.prompt.trim()) {
          message = '请输入提示词'
        }
        if (!videoPublicForm.img1) {
          message = '请上传图片'
        } else if (videoPublicForm.img1.includes('violation')) {
          message = '图片内容违规，请更换图片'
        }
        break;
      }
      case 'effect_video': {
        const activeTemplate = this.getActiveItem(formType, 'effect')
        if (activeTemplate.show_mode !== 'single' && activeTemplate.img_2_able && !videoPublicForm.img2) {
          message = '请上传第二张图片'
        }
        if (activeTemplate.img_1_able && !videoPublicForm.img1) {
          message = '请上传第一张图片'
        }
        if ((activeTemplate.img_1_able && videoPublicForm.img1.includes('violation')) || (activeTemplate.img_2_able && videoPublicForm.img2.includes('violation'))) {
          message = '图片内容违规，请更换图片'
        }
        break
      }
      case 'image_video_reference': {
        if (!videoPublicForm.img1) {
          message = '请上传第一张图片'
        }
        if (videoPublicForm.img1.includes('violation') || videoPublicForm.img2.includes('violation') || videoPublicForm.img_n?.some(item => item.includes('violation'))) {
          message = '图片内容违规，请更换图片'
        }
        if ('prompt' in videoPublicForm && !videoPublicForm.prompt.trim()) {
          message = '请输入提示词'
        }
        break
      }
      case 'image_video_part': {
        if (!videoPublicForm.img1) {
          message = '请上传第一张图片'
        } else if (videoPublicForm.img1.includes('violation') || videoPublicForm.img2.includes('violation')) {
          message = '图片内容违规，请更换图片'
        }
        if ('prompt' in videoPublicForm && !videoPublicForm.prompt.trim()) {
          message = '请输入提示词'
        }
        break
      }
      case 'image_video_frame': {
        const form = aigcFormStore.getFormByType('image_video_frame')
        if (!form.frame_img) {
          message = '请上传图片'
        } else if (form.frame_img.includes('violation')) {
          message = '图片内容违规，请更换图片'
        }
        if ('frame_prompt' in form && !form.frame_prompt.trim()) {
          message = '请输入提示词'
        }
      }
    }

    if (!message) {
      return true
    }
    HelpOther.toast(message, 'error')
  }

  closeDropdown = () => {
    this.setShownDropdown('')
  }

  onShowDopdown = (formType, type) => {

    if (aigcFormStore.shownDropdown !== type && type !== 'ds') {
      appLogger.log({
        key: formType.includes('video') ? 'aibot_video_creation' : 'aibot_photo_creation',
        action: 'click',
        type: type === 'bgm' ? 'background_music' : type === 'style' ? 'more_style' : 'video_model',
        ext: formType
      });
    }

    this.setShownDropdown(aigcFormStore.shownDropdown === type ? '' : type);
    document.body.addEventListener('click', this.closeDropdown, { once: true })
  }

  imitateVideo = async (mid, bizType) => {

    const MODE_MAP = {
      'part': 'image_video_part',
      'frame': 'image_video_frame',
      'reference': 'image_video_reference',
    }

    const { data } = await request.post('/video/detail?appsource=so&device_platform=Web', { mid: mid }).catch(e => {
      console.log(e);
      HelpOther.toast('详情获取失败', 'error')
      throw e
    })

    if (data.template_id) {
      bizType = 'effect_video'
    }

    const formType = (bizType !== 'effect_video' && MODE_MAP[data.mode]) || bizType;

    aigcFormStore.resetForm(formType);
    aigcFormStore.resetForm('video_public');

    const params: any = {};
    const publicParams: any = {};

    const allParams = { ...data, prompt: data.prompt_origin || data.prompt, deepseek_optimize: 0 };

    Object.keys(aigcFormStore.getFormByType('video_public')).forEach(key => {
      if (key in allParams) {
        publicParams[key] = allParams[key];
      }
    })

    Object.keys(aigcFormStore.getFormByType(bizType)).forEach(key => {
      if (key in allParams) {
        params[key] = allParams[key];
      }
    });

    // 特效创作同款时 清除图片
    if (data.template_id) {
      params.image = ''
      delete publicParams.prompt;
      bizType = 'effect_video';
      params.mode = '';
    }

    if (!publicParams.img1 && allParams.image) {
      publicParams.img1 = allParams.image;
    }

    // 视频帧是单独的frame_img字段
    if (data.mode === 'frame') {
      params.frame_img = publicParams.img1 || publicParams.image
      params.frame_prompt = publicParams.prompt
      publicParams.prompt = ''
      publicParams.img1 = ''
      publicParams.image = ''
    }
    aigcFormStore.fetchVideoListData().then(() => {
      aigcFormStore.setForm('video_public', publicParams);
      aigcFormStore.setForm(formType, params);
    })

    GenerateVideoStore.setSource('work');
    GenerateVideoStore.setActiveTask()
    GenerateVideoStore.setHistoryActiveTask(bizType, null)
    stateStore.setLocation(`/generate/video?form_type=${formType}`);

  }
}

export const aigcFormStore = new AigcFormStore();
